import styled from 'styled-components';
import Heading from '../../components/Heading/Heading';

/**
 * Hero image container with optional dark gradient for non-box mode
 * Optimised for an image in 16:5 ratio on all but small width screens
 */
export const Container = styled.div`
  position: relative;
  overflow: hidden;
  background-image: ${(props) => {
    const gradient = !props.$backgroundBox ? `linear-gradient(to bottom left, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.75))` : '';
    const imageUrl = props.$image && props.$image.trim() ? `url('${props.$image}')` : '';
    if (gradient && imageUrl) {
      return `${gradient}, ${imageUrl}`;
    } else if (gradient) {
      return gradient;
    } else if (imageUrl) {
      return imageUrl;
    } else {
      return 'none';
    }
  }};

  padding-bottom: 10px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: flex-end;

  /* Max heights for large screens */
  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.m}) {
    max-height: 410px;
  }
  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.l}) {
    max-height: 560px;
  }
  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.xl}) {
    max-height: 636px;
  }

  &::before {
    content: '';
    /* phones */
    padding-top: 90%;
    /* tablet */
    @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.m}) {
      padding-top: 40%;
    }
    /* everything else */
    @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.l}) {
      padding-top: 31.25%;
    }
  }
`;

export const InnerContainer = styled.div`
  max-width: 100%;
  margin-right: auto;
  margin-left: auto;
  flex-grow: 1;
  z-index: 2;

  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.l}) {
    max-width: ${(props) => props.theme.theme_vars.breakpoints.l};
  }
`;

/**
 * Overlay div containing the headline, text, link
 */
export const Overlay = styled.div`
  ${(props) => props.theme.fontStyles}
  text-align: left;
  margin-bottom: ${(props) => props.theme.theme_vars.spacingSizes.small};
  margin-left: 15px;
  margin-right: 15px;
  padding: 25px 25px 0 25px;
  color: ${(props) =>
    props.$backgroundBox ? props.theme.theme_vars.colours.black : props.theme.theme_vars.colours.white};
  background-color: ${(props) =>
    props.$backgroundBox ? props.theme.theme_vars.colours.grey_light + 'F2' : `transparent`};
  box-shadow: ${(props) =>
    props.$backgroundBox
      ? `0px -4px 0px 0px ` + props.theme.theme_vars.colours.action + ` inset, 0px 4px 15px rgba(0, 0, 0, 0.11)`
      : `none`};

  border-radius: 5px;
  overflow: hidden;

  /* default - phones */
  max-width: calc(100% - 30px);

  /* tablets */
  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.m}) {
    max-width: 65%;
    margin-left: ${(props) => props.theme.theme_vars.spacingSizes.large};
    margin-right: 0;
    margin-bottom: ${(props) => props.theme.theme_vars.spacingSizes.medium};
  }

  /* desktop */
  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.l}) {
    max-width: 50%;
    margin-left: 0;
    margin-bottom: ${(props) => props.theme.theme_vars.spacingSizes.x_large};
  }
`;

export const Headline = styled(Heading)`
  margin: 0;
  color: ${(props) =>
    props.$backgroundBox ? props.theme.theme_vars.colours.black : props.theme.theme_vars.colours.white};

  /* default - phones */
  font-size: 22px;

  /* tablets */
  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.m}) {
    font-size: 32px;
  }

  /* desktop */
  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.l}) {
    font-size: 44px;
  }
`;

export const Content = styled.div`
  margin-top: 10px;
  p,
  div {
    margin-bottom: 10px;
  }
`;

/**
 * In non-box mode the link is not a button, and includes a glow when hovering/focused
 */
export const CallToActionLink = styled.a`
  ${(props) => props.theme.fontStyles}
  text-decoration: underline;
  color: ${(props) => props.theme.theme_vars.colours.white} !important;
  width: 100%;
  padding: 0;
  margin-top: 10px;
  display: inline-block;
  outline: none;

  &:hover {
    text-decoration-style: dotted;
    text-shadow: 2px 2px 4px rgba(150, 150, 150, 0.5), -2px 2px 4px rgba(150, 150, 150, 0.5),
      2px -2px 4px rgba(150, 150, 150, 0.5), -2px -2px 4px rgba(150, 150, 150, 0.5);
  }
  &:focus {
    ${(props) => props.theme.linkStylesFocus};
  }
  &:active {
    transform: translate(3px);
  }
`;

export const BreadcrumbLink = styled.a`
  ${(props) => props.theme.fontStyles}
  ${(props) => props.theme.linkStyles}
  color: ${(props) =>
    props.$backgroundBox ? props.theme.theme_vars.colours.action : props.theme.theme_vars.colours.white};
  margin-bottom: ${(props) => props.theme.theme_vars.spacingSizes.small};
  display: block;

  &:hover,
  &:focus {
    text-decoration-style: dashed;
    text-shadow: ${(props) =>
      props.$backgroundBox
        ? 'none'
        : `2px 2px 4px rgba(150, 150, 150, 0.5), -2px 2px 4px rgba(150, 150, 150, 0.5),
      2px -2px 4px rgba(150, 150, 150, 0.5), -2px -2px 4px rgba(150, 150, 150, 0.5)`};
  }
`;

export const Search = styled.div`
  margin: ${(props) => props.theme.theme_vars.spacingSizes.medium} 0;
`;

export const VideoContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
`;

export const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

export const PlayPauseButton = styled.button`
  position: absolute;
  display: none;
  @media screen and (min-width: ${(props) => props.theme.theme_vars.breakpoints.l}) {
    bottom: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid white;
    color: white;
    font-size: 32px;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    cursor: pointer;
    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }
    &:focus {
      outline: none;
      background: ${(props) => props.theme.theme_vars.colours.focus};
      color: ${(props) => props.theme.theme_vars.colours.black};
      box-shadow: 0 0 0 2px ${(props) => props.theme.theme_vars.colours.focus}, 0 0 0 4px ${(props) => props.theme.theme_vars.colours.black};
      -webkit-box-shadow: 0 0 0 2px ${(props) => props.theme.theme_vars.colours.focus}, 0 0 0 4px ${(props) => props.theme.theme_vars.colours.black};
      -moz-box-shadow: 0 0 0 2px ${(props) => props.theme.theme_vars.colours.focus}, 0 0 0 4px ${(props) => props.theme.theme_vars.colours.black};
    }
  }
`;
